import { alpha, type Theme } from '@mui/material/styles';

/**
 * Styles for the app settings page. Tiles, pills and borders are the ones the teams and POI
 * type pages use, so the page reads as part of the same section; what is its own is the
 * "set for this team" accent, which has to be legible at a glance down a long list.
 */

export const tileStyles = {
  width: 36,
  height: 36,
  flexShrink: 0,
  borderRadius: '.5rem',
  backgroundColor: '#26272F',
  border: '1px solid rgb(54, 56, 67)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#B5B7C8',
  fontSize: 18,
};

export const heroIconStyles = {
  ...tileStyles,
  width: 52,
  height: 52,
  borderRadius: '.6rem',
  fontSize: 26,
};

export const countPillStyles = {
  minWidth: 22,
  px: 0.9,
  lineHeight: '20px',
  textAlign: 'center',
  borderRadius: '999px',
  backgroundColor: '#26272F',
  border: '1px solid rgb(54, 56, 67)',
  color: 'text.secondary',
  fontSize: 11.5,
  fontWeight: 600,
};

export const changedPillStyles = {
  ...countPillStyles,
  color: 'primary.light',
  borderColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.45),
  backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12),
};

/**
 * "Set here" as a chip, not a switch: every card below uses the same switch for a setting's
 * value, so a switch up here read as one more setting. It uses the rows' own "• Set here"
 * wording, so the two read as one vocabulary.
 */
export const filterChipStyles = {
  px: 1.25,
  py: 0.4,
  height: 30,
  gap: 1,
  alignItems: 'center',
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  color: 'text.secondary',
  fontSize: 13,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  transition: 'background-color 120ms ease, border-color 120ms ease',
  cursor: 'pointer',
  userSelect: 'none',
  '& i': { fontSize: 16 },
  '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.light', outlineOffset: 1 },
};

export const filterChipActiveStyles = {
  color: 'primary.light',
  borderColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.55),
  backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12),
};

/**
 * The toolbar, held at the top of the scrolling page as one row: the group tabs on the left,
 * the filter and search on the right. One row because it stays on screen — two made 95px of
 * chrome for a search used once a visit. Painted in `grey.900`, the colour the page actually
 * shows (`<main>` is transparent over the body; `background.default` is a shade lighter and
 * drew the bar as a box). Below `lg` the small navbar is fixed, so it sticks under that; below
 * `md` the tools take a line of their own above the tabs.
 */
export const toolbarStyles = {
  position: 'sticky',
  top: { xs: 65, lg: 0 },
  zIndex: 2,
  pt: 0.5,
  mb: 1.5,
  alignItems: 'center',
  flexWrap: { xs: 'wrap', md: 'nowrap' },
  backgroundColor: 'grey.900',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

/**
 * The groups as tabs, the one in view underlined on the bar's bottom edge. Not a second
 * sidebar: the app's own navigation owns the left edge, and a nested one beside it read as
 * loose text. Text only — the cards carry the icons, and with them the six tabs no longer fit
 * beside the tools in Turkish at 1440px. Pulled left by a tab's padding so the first label
 * lines up with the cards.
 * Scrolls sideways when narrow, which is why the underline sits inside each tab: the
 * scroller would clip one hung below it.
 */
export const groupTabsStyles = {
  flex: 1,
  minWidth: 0,
  ml: -1.25,
  gap: 0.25,
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
};

/** Only while some tabs are out of view: a fade says there is more to scroll to. */
export const groupTabsFadeStyles = {
  maskImage: 'linear-gradient(to right, #000 92%, transparent)',
};

/** The filter and the search, kept together at the row's right end. */
export const toolbarClusterStyles = {
  flexShrink: 0,
  gap: 1,
  alignItems: 'center',
  pl: 2,
  order: { xs: -1, md: 0 },
  width: { xs: '100%', md: 'auto' },
  pb: { xs: 1, md: 0 },
};

/**
 * A compact search in the look of the page's selects and segmented controls, widening while
 * it is in use. The heavy outlined text field it replaces outweighed the tabs beside it.
 */
export const searchStyles = {
  height: 32,
  width: { xs: 'auto', md: 200 },
  flex: { xs: 1, md: 'none' },
  px: 1,
  gap: 0.75,
  display: 'flex',
  alignItems: 'center',
  borderRadius: 1,
  backgroundColor: '#1B1D24',
  border: '1px solid rgb(54, 56, 67)',
  transition: 'width 160ms ease, border-color 120ms ease',
  '& > i': { fontSize: 16, color: 'text.secondary', flexShrink: 0 },
  '&:focus-within': { width: { md: 280 } },
  '&:focus-within .search-hint': { display: 'none' },
};

export const searchActiveStyles = {
  width: { md: 280 },
  borderColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.55),
};

/** The `/` shortcut, shown at rest. */
export const searchHintStyles = {
  px: 0.6,
  lineHeight: '16px',
  borderRadius: 0.5,
  border: '1px solid rgb(54, 56, 67)',
  color: 'text.secondary',
  fontSize: 11,
  fontFamily: 'inherit',
  flexShrink: 0,
};

export const groupTabStyles = {
  px: 1.25,
  pt: 1,
  pb: 1.25,
  gap: 1,
  flexShrink: 0,
  alignItems: 'center',
  cursor: 'pointer',
  userSelect: 'none',
  color: 'text.secondary',
  fontSize: 14,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  position: 'relative',
  transition: 'color 120ms ease',
  '& i': { fontSize: 18 },
  '&:hover': { color: 'text.primary' },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 10,
    right: 10,
    bottom: 0,
    height: 2,
    borderRadius: '2px 2px 0 0',
    backgroundColor: 'transparent',
    transition: 'background-color 120ms ease',
  },
};

export const groupTabActiveStyles = {
  color: 'text.primary',
  '&::after': { backgroundColor: 'primary.main' },
};

export const groupHeaderStyles = {
  px: 2.25,
  py: 1.75,
  gap: 1.5,
  alignItems: 'center',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

export const sectionHeaderStyles = {
  px: 2.25,
  pt: 1.75,
  pb: 0.5,
  fontSize: 12,
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: 0.5,
};

/**
 * One setting. The left edge carries the accent: transparent while the value is inherited,
 * the brand colour once the team sets its own — the same gutter mark an editor puts beside
 * a changed line.
 */
export const settingRowStyles = {
  pl: 2,
  pr: 1.25,
  py: 1.25,
  gap: 2,
  minHeight: 60,
  alignItems: 'center',
  borderLeft: '3px solid transparent',
  transition: 'background-color 120ms ease, border-color 120ms ease, opacity 120ms ease',
  '&:not(:last-of-type)': {
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
  },
  '&:hover': { backgroundColor: 'action.hover' },
};

export const settingRowChangedStyles = {
  borderLeftColor: 'primary.main',
  backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.045),
};

/** A setting changed and not saved yet: the row's edge in the warning colour, like its dot. */
export const settingRowPendingStyles = {
  borderLeftColor: 'warning.main',
};

/** The reader's default team in the picker: a quiet tag after its name. */
export const defaultMarkStyles = {
  px: 0.75,
  lineHeight: '18px',
  borderRadius: 0.75,
  border: '1px solid',
  borderColor: 'divider',
  color: 'text.secondary',
  fontSize: 11,
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

export const pendingDotStyles = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: 'warning.main',
  flexShrink: 0,
};

/**
 * Save and Discard, held at the bottom of the page while anything is unsaved, so they stay in
 * reach wherever in the list the change was made. Painted like the toolbar at the top.
 */
export const saveBarStyles = {
  position: 'sticky',
  bottom: 0,
  zIndex: 2,
  mt: 2,
  px: 2,
  py: 1.25,
  gap: 1.5,
  alignItems: 'center',
  backgroundColor: 'grey.900',
  borderTop: '1px solid',
  borderColor: 'divider',
};

/** A setting that does nothing until another is changed — still editable, just quieter. */
export const settingRowDormantStyles = {
  '& .setting-label, & .setting-control': { opacity: 0.55 },
};

export const colorSwatchStyles = {
  width: 26,
  height: 26,
  p: 0,
  borderRadius: '50%',
  border: '2px solid',
  borderColor: 'transparent',
  boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.18)',
  cursor: 'pointer',
  transition: 'transform 120ms ease, border-color 120ms ease',
  '&:hover:not(:disabled)': { transform: 'scale(1.08)' },
  '&:disabled': { cursor: 'default' },
};

export const colorSwatchSelectedStyles = {
  borderColor: 'background.default',
  // The ring is a shadow, not an outline: the global focus styles reset a focused button's
  // outline, which hid the ring until the swatch just clicked lost focus.
  boxShadow: (theme: Theme) => `inset 0 0 0 1px rgba(255, 255, 255, 0.18), 0 0 0 2px ${theme.palette.primary.light}`,
};

/** A segmented control: the chosen option filled with the brand tint, the rest quiet. */
export const segmentedStyles = {
  backgroundColor: '#1B1D24',
  '& .MuiToggleButton-root': {
    px: 1.5,
    py: 0.5,
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 500,
    color: 'text.secondary',
    borderColor: 'rgb(54, 56, 67)',
    '&.Mui-selected': {
      color: 'primary.light',
      backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.16),
      '&:hover': { backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.22) },
    },
  },
};

/**
 * Width options, set on the group rather than on each button: the segmented control's own
 * padding is a group-level selector too, and a button-level one loses to it, which squeezed
 * the previews into dots.
 */
export const widthOptionStyles = {
  '& .MuiToggleButton-root': {
    width: 34,
    height: 30,
    px: 0,
  },
};

export const valueChipStyles = {
  minWidth: 52,
  px: 1,
  py: 0.25,
  textAlign: 'center',
  borderRadius: 1,
  backgroundColor: '#1B1D24',
  border: '1px solid rgb(54, 56, 67)',
  fontSize: 13,
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
};

export const selectStyles = {
  minWidth: 200,
  fontSize: 14,
  backgroundColor: '#1B1D24',
  '& .MuiSelect-select': { py: 0.75, display: 'flex', alignItems: 'center', gap: 1 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgb(54, 56, 67)' },
};

export const readOnlyBannerStyles = {
  px: 2,
  py: 1.25,
  gap: 1.25,
  alignItems: 'center',
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  color: 'text.secondary',
  fontSize: 14,
  backgroundColor: (theme: Theme) => alpha(theme.palette.common.black, 0.16),
  '& i': { fontSize: 18 },
};

export const buttonStyles = {
  textTransform: 'none',
  fontWeight: 600,
};
